import { ClientTaxonomyRefreshPeriod } from './CustomBlock';
import { isValidClientTaxonomyCollectionKey } from './ClientTaxonomyCollection';

const CLIENT_TAXONOMY_TYPE = 'client_taxonomy';
const CONTENT_PRODUCING_TYPES = new Set(['image', 'text', 'remote']);
const REFRESH_PERIODS = new Set<ClientTaxonomyRefreshPeriod>(['PT1H', 'P1D', 'P7D']);

const isObject = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const isNonEmptyString = (value: unknown): value is string => {
    return typeof value === 'string' && value.trim().length > 0;
};

export const getCustomBlockConfigurationValidationError = (
    value: string | undefined,
    allowClientTaxonomy: boolean
): string | null => {
    if (!value) {
        return null;
    }

    let blocks: unknown;
    try {
        blocks = JSON.parse(value);
    } catch {
        return 'Custom block configuration must be valid JSON.';
    }

    if (!Array.isArray(blocks)) {
        return 'Custom block configuration must be an array.';
    }

    for (const block of blocks) {
        if (!isObject(block) || !Array.isArray(block.pipeline)) {
            continue;
        }

        const clientTaxonomySteps = block.pipeline.filter(
            pipelineStep => isObject(pipelineStep) && pipelineStep.type === CLIENT_TAXONOMY_TYPE
        );

        if (clientTaxonomySteps.length === 0) {
            continue;
        }

        if (!allowClientTaxonomy) {
            return 'ClientTaxonomy can only be configured for an existing project.';
        }

        if (clientTaxonomySteps.length > 1) {
            return 'An Interpretation Block can contain only one ClientTaxonomy step.';
        }

        if (
            !block.pipeline.some(
                pipelineStep =>
                    isObject(pipelineStep) &&
                    typeof pipelineStep.type === 'string' &&
                    CONTENT_PRODUCING_TYPES.has(pipelineStep.type)
            )
        ) {
            return 'An Interpretation Block with ClientTaxonomy must contain an Image, Text, or Remote step.';
        }

        const clientTaxonomyStep = clientTaxonomySteps[0];

        if (clientTaxonomyStep.schema_version !== 1) {
            return 'ClientTaxonomy schema version must be 1.';
        }

        if (!isNonEmptyString(clientTaxonomyStep.application_id)) {
            return 'ClientTaxonomy Application is required.';
        }

        if (!isObject(clientTaxonomyStep.reference_data)) {
            return 'ClientTaxonomy Reference Data is required.';
        }

        if ('data_set' in clientTaxonomyStep.reference_data || 'script_id' in clientTaxonomyStep.reference_data) {
            return 'ClientTaxonomy Reference Data must use the collections array.';
        }

        const collections = clientTaxonomyStep.reference_data.collections;
        if (!Array.isArray(collections) || collections.length === 0) {
            return 'ClientTaxonomy requires at least one Reference Data collection.';
        }

        const collectionKeys = new Set<string>();
        for (const collection of collections) {
            if (!isObject(collection)) {
                return 'Each ClientTaxonomy Reference Data collection must be an object.';
            }

            if (!isNonEmptyString(collection.data_set)) {
                return 'ClientTaxonomy Data Set is required.';
            }

            if (!isValidClientTaxonomyCollectionKey(collection.data_set)) {
                return 'ClientTaxonomy Data Set must be a lower-snake-case key of at most 64 characters.';
            }

            if (collectionKeys.has(collection.data_set)) {
                return 'ClientTaxonomy Data Sets must be unique.';
            }
            collectionKeys.add(collection.data_set);

            if (!isNonEmptyString(collection.script_id)) {
                return `ClientTaxonomy Script is required for ${collection.data_set}.`;
            }
        }

        if (typeof clientTaxonomyStep.use_cached_data !== 'boolean') {
            return 'ClientTaxonomy Use cached data must be true or false.';
        }

        if (
            typeof clientTaxonomyStep.refresh_period !== 'string' ||
            !REFRESH_PERIODS.has(clientTaxonomyStep.refresh_period as ClientTaxonomyRefreshPeriod)
        ) {
            return 'ClientTaxonomy Refresh Period must be 60 minutes, 1 day, or 1 week.';
        }
    }

    return null;
};

export const validateCustomBlockConfiguration = (
    value: string | undefined,
    allowClientTaxonomy: boolean
): Promise<void> => {
    const error = getCustomBlockConfigurationValidationError(value, allowClientTaxonomy);
    return error ? Promise.reject(new Error(error)) : Promise.resolve();
};
